import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import Popover from '../Popover';
import RawText from '../Text/RawText';
import { RenderChildrenParams, RenderElementParams } from '../common/Ellipsis';

import { dataHooks, TIMEOUT } from './constants';
import type { TooltipProps, TooltipActions } from './Tooltip.types';
import { ZIndex } from '../common/ZIndex';

import { st, classes } from './Tooltip.st.css';
import { useEventCallback } from '../common/useEventCallback/useEventCallback';

const Tooltip = forwardRef<TooltipActions, TooltipProps>(
  (
    {
      onShow = () => ({}),
      onHide = () => ({}),
      size = 'medium',
      className,
      placement = 'top',
      exitDelay = 0,
      enterDelay = 0,
      moveArrowTo,
      appendTo = 'window',
      flip = true,
      fixed = false,
      maxWidth = 204,
      fluid,
      moveBy,
      disabled,
      children,
      zIndex = ZIndex.tooltip,
      textAlign = 'start',
      content = '',
      inline,
      'aria-describedby': ariaDescribedBy,
      'aria-labelledby': ariaLabelledBy,
      interactive = true,
      ...rest
    }: TooltipProps,
    ref,
  ) => {
    const [shown, setShown] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    const open = useEventCallback(() => {
      setShown(true);
      onShow?.();
    });

    const close = useEventCallback(() => {
      setShown(false);
      onHide?.();
    });

    useImperativeHandle(ref, () => ({
      open,
      close,
      getContentElement: () => contentRef.current,
    }));

    const isDisabled = disabled ?? false;

    const onFocus = useEventCallback(
      (
        event: React.FocusEvent,
        handlers?: { focus: () => void; blur: () => void },
      ) => {
        const focusableHOC = handlers && handlers.focus;
        const target = event.target;

        try {
          if (
            process.env.NODE_ENV !== 'test' &&
            target instanceof Element &&
            !target.matches(':focus-visible')
          ) {
            handlers?.blur();
            return;
          }
        } catch (e) {
          // browser does not support `matches(':focus-visible')` fallback to original focus behavior
        }

        open();
        return focusableHOC ? handlers.focus() : null;
      },
    );

    const onBlur = useEventCallback(
      (_event: React.FocusEvent, handlers?: { blur: () => void }) => {
        const focusableHOC = handlers && handlers.blur;
        close();
        return focusableHOC ? handlers.blur() : null;
      },
    );

    const handleKeyDown = useEventCallback((event: KeyboardEvent) => {
      if (event.key === 'Escape' && shown) {
        close();
      }
    });

    useEffect(() => {
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [handleKeyDown]);

    const triggerElement = useMemo(() => {
      if (typeof children === 'string' || !children) {
        return children;
      }

      if (React.isValidElement(children)) {
        const ariaProps = ariaLabelledBy
          ? { 'aria-labelledby': ariaLabelledBy }
          : { 'aria-describedby': ariaDescribedBy };

        return React.cloneElement(children as any, {
          ...(isDisabled ? {} : { onFocus, onBlur }),
          ...ariaProps,
        });
      }

      if (typeof children === 'function') {
        return children({
          onFocus,
          onBlur,
        });
      }

      return;
    }, [
      children,
      isDisabled,
      onFocus,
      onBlur,
      ariaDescribedBy,
      ariaLabelledBy,
    ]);

    const contentElement = useMemo(() => {
      const textSize = size === 'small' ? 'tiny' : 'small';

      return (
        <div style={{ textAlign }}>
          {typeof content === 'string' ? (
            // @ts-ignore
            <RawText
              dataHook={dataHooks.tooltipText}
              size={textSize}
              light
              className={classes.text}
              renderChildren={({ text }: RenderChildrenParams) => text}
              renderElement={({ element }: RenderElementParams) => element}
            >
              {content}
            </RawText>
          ) : (
            content
          )}
        </div>
      );
    }, [content, textAlign, size]);

    return (
      <Popover
        role="tooltip"
        showArrow={size !== 'small'}
        className={st(classes.root, { size, inline }, className)}
        placement={placement}
        shown={isDisabled ? false : shown}
        onMouseEnter={isDisabled ? undefined : open}
        onMouseLeave={close}
        onClickOutside={close}
        onEscPress={close}
        data-size={size}
        hideDelay={exitDelay}
        showDelay={enterDelay}
        moveBy={moveBy}
        moveArrowTo={moveArrowTo}
        appendTo={appendTo}
        id={ariaLabelledBy || ariaDescribedBy}
        flip={flip}
        fixed={fixed}
        maxWidth={maxWidth}
        fluid={fluid}
        disableClickOutsideWhenClosed
        zIndex={zIndex}
        interactive={interactive}
        {...rest}
        theme="dark"
        timeout={TIMEOUT}
      >
        <Popover.Element>{triggerElement}</Popover.Element>
        <Popover.Content>
          <div ref={contentRef} style={{ maxWidth }}>
            {contentElement}
          </div>
        </Popover.Content>
      </Popover>
    );
  },
);

Tooltip.displayName = 'Tooltip';

export default Tooltip;
